import { useTargetingBlockingRulesSchemaFetchContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { useIsMounted } from '@openx/utils/hooks/useIsMounted.ts';
import { isObjectRecord } from '@openx/utils/lib/typeGuards/isObjectRecord.ts';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createTargetingRulesValidator } from './createTargetingRulesValidator.ts';
import type { TargetingBlockingRulesInput } from './normalizeTargetingForSchema.ts';
import { normalizeTargetingForSchema } from './normalizeTargetingForSchema.ts';
import type { BlockedTargetingFieldsMap, JsonSchema, TargetingRuleSchemaNode } from './types.ts';

type TargetingRulesValidator = Awaited<ReturnType<typeof createTargetingRulesValidator>>;

type UseTargetingBlockingRulesParams = {
  targeting: TargetingBlockingRulesInput | null | undefined;
  enabled?: boolean;
};

type UseTargetingBlockingRulesResult = {
  fieldsWithErrors: BlockedTargetingFieldsMap;
  blockedTargetingFields: BlockedTargetingFieldsMap;
  isTargetingBlockingRulesSchemaLoading: boolean;
  isTargetingBlockingRulesValidationPending: boolean;
};

type TargetingRuleBlockResult = {
  field: string;
  message: string;
};

type SchemaFieldToUIFieldMap = typeof schemaFieldToUIFieldMap;

const schemaFieldToUIFieldMap = {
  url_targeting: TargetingFields.URL_TARGETING,
  app_inventory: TargetingFields.APP_INVENTORY,
  technographic: TargetingFields.TECHNOLOGY_AND_DEVICES,
  metacategory: TargetingFields.METACATEGORY,
  video: TargetingFields.VIDEO,
  viewability: TargetingFields.VIEWABILITY,
  vtr: TargetingFields.VIEW_THROUGH_RATE,
  instream_content: TargetingFields.CONTENT_OBJECT,
  content: TargetingFields.INVENTORY_AND_CONTENT,
  geographic: TargetingFields.GEOGRAPHIC,
  audience: TargetingFields.AUDIENCE,
  categories: TargetingFields.CATEGORY,
  inventory_format: TargetingFields.FORMAT_TYPE,
  rendering_context: TargetingFields.INVENTORY_FORMAT,
  ads: TargetingFields.AD_SIZES,
  advanced_targeting: TargetingFields.ADVANCED_TARGETING,
  custom: TargetingFields.CUSTOM_VARIABLES,
  contextual: TargetingFields.CONTEXTUAL_SEGMENTS,
} as const;

const hasRequiredKeys = (value: unknown, required: TargetingRuleSchemaNode['required']): boolean => {
  if (!required) {
    return true;
  }

  if (!isObjectRecord(value)) {
    return false;
  }

  return required.every(key => value[key] !== undefined);
};

const matchesSchemaNode = (value: unknown, schemaNode: TargetingRuleSchemaNode | undefined): boolean => {
  if (!schemaNode) {
    return true;
  }

  if (!hasRequiredKeys(value, schemaNode.required)) {
    return false;
  }

  if ('const' in schemaNode) {
    return value === schemaNode.const;
  }

  if (Array.isArray(schemaNode.enum)) {
    return schemaNode.enum.includes(value);
  }

  if (schemaNode.properties) {
    if (!isObjectRecord(value)) {
      return false;
    }

    return Object.entries(schemaNode.properties).every(([key, childSchema]) =>
      matchesSchemaNode(value[key], childSchema)
    );
  }

  return true;
};

const getBlockedFieldsFromSchemaNode = (
  schemaNode: TargetingRuleSchemaNode,
  path: string[] = []
): TargetingRuleBlockResult[] => {
  const currentPath = path.join('.');

  const ownBlockedField =
    currentPath && typeof schemaNode['x-tooltip-message'] === 'string'
      ? [
          {
            field: currentPath,
            message: schemaNode['x-tooltip-message'],
          },
        ]
      : [];

  const propertyBlockedFields = schemaNode.properties
    ? Object.entries(schemaNode.properties).flatMap(([field, childSchemaNode]) =>
        getBlockedFieldsFromSchemaNode(childSchemaNode, [...path, field])
      )
    : [];

  const anyOfBlockedFields = schemaNode.anyOf
    ? schemaNode.anyOf.flatMap(childSchemaNode => getBlockedFieldsFromSchemaNode(childSchemaNode, path))
    : [];

  return [...ownBlockedField, ...propertyBlockedFields, ...anyOfBlockedFields];
};

const getActiveBlockedFields = (schema: JsonSchema, data: unknown): TargetingRuleBlockResult[] => {
  if (!schema.definitions) {
    return [];
  }

  return Object.values(schema.definitions).reduce<TargetingRuleBlockResult[]>((accumulator, definition) => {
    const ifBranch = definition.if;
    const thenBranch = definition.then;

    if (!ifBranch || !thenBranch?.properties) {
      return accumulator;
    }

    if (!matchesSchemaNode(data, ifBranch)) {
      return accumulator;
    }

    const blockedFieldsFromDefinition = Object.entries(thenBranch.properties).flatMap(([field, propertySchema]) =>
      getBlockedFieldsFromSchemaNode(propertySchema, [field])
    );

    accumulator.push(...blockedFieldsFromDefinition);

    return accumulator;
  }, []);
};

const resolveUIField = (field: string): string => {
  const pathParts = field.split('.');

  // Unlike most targeting params, UI field paths aren't stored with 'targeting',
  // for example: `targeting.metacategory.keywords` -> `metacategory.keywords`
  const [rootField, ...nestedPath] = pathParts[0] === 'targeting' ? pathParts.slice(1) : pathParts;

  const uiRootField = schemaFieldToUIFieldMap[rootField as keyof SchemaFieldToUIFieldMap] ?? rootField;

  return nestedPath.length ? [uiRootField, ...nestedPath].join('.') : uiRootField;
};

const mapBlockedFields = (fields: TargetingRuleBlockResult[]): BlockedTargetingFieldsMap => {
  const result: BlockedTargetingFieldsMap = {};

  fields.forEach(({ field, message }) => {
    const uiField = resolveUIField(field);

    if (!result[uiField]) {
      result[uiField] = message;
    }
  });

  return result;
};

export const useTargetingBlockingRules = ({
  targeting,
  enabled = true,
}: UseTargetingBlockingRulesParams): UseTargetingBlockingRulesResult => {
  const [blockedTargetingFields, setBlockedTargetingFields] = useState<BlockedTargetingFieldsMap>({});

  const [fieldsWithErrors, setFieldsWithErrors] = useState<BlockedTargetingFieldsMap>({});
  const [isTargetingBlockingRulesValidationPending, setIsTargetingBlockingRulesValidationPending] = useState(false);

  const useTargetingBlockingRulesSchemaFetch = useTargetingBlockingRulesSchemaFetchContext();

  const { targetingBlockingRulesSchema: rawTargetingBlockingRulesSchema, isTargetingBlockingRulesSchemaLoading } =
    useTargetingBlockingRulesSchemaFetch({ enabled });

  const targetingBlockingRulesSchema = rawTargetingBlockingRulesSchema as JsonSchema | null | undefined;

  const validatorRef = useRef<TargetingRulesValidator | null>(null);
  const validatorSchemaRef = useRef<JsonSchema | null>(null);

  const normalizedTargeting = useMemo(() => normalizeTargetingForSchema(targeting), [targeting]);

  const activeBlockedFields = useMemo(() => {
    if (!enabled || !targetingBlockingRulesSchema) {
      return [];
    }

    return getActiveBlockedFields(targetingBlockingRulesSchema, normalizedTargeting);
  }, [enabled, targetingBlockingRulesSchema, normalizedTargeting]);

  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    const validateTargeting = async () => {
      if (!enabled || !targetingBlockingRulesSchema) {
        setBlockedTargetingFields({});
        setFieldsWithErrors({});
        setIsTargetingBlockingRulesValidationPending(false);

        return;
      }

      setIsTargetingBlockingRulesValidationPending(true);

      try {
        if (validatorSchemaRef.current !== targetingBlockingRulesSchema) {
          validatorRef.current = await createTargetingRulesValidator(targetingBlockingRulesSchema);
          validatorSchemaRef.current = targetingBlockingRulesSchema;
        }

        const validator = validatorRef.current;

        if (!isMounted() || !validator) {
          return;
        }

        const { blockedFields: validatedFields } = validator(normalizedTargeting);

        setBlockedTargetingFields(mapBlockedFields(activeBlockedFields));
        setFieldsWithErrors(mapBlockedFields(validatedFields));
      } finally {
        if (isMounted()) {
          setIsTargetingBlockingRulesValidationPending(false);
        }
      }
    };

    void validateTargeting();
  }, [enabled, normalizedTargeting, targetingBlockingRulesSchema, activeBlockedFields, isMounted]);

  return {
    fieldsWithErrors,
    blockedTargetingFields,
    isTargetingBlockingRulesSchemaLoading,
    isTargetingBlockingRulesValidationPending,
  };
};
